import React from "react";
import styled from "styled-components";
import { Check } from "../HearingBundles/components/Icons.js";
import Modal from "../../../booking_dashboard/components/Modal";
import COLORS from "../../../../assets/Colors";
import CaseLifeCycle from "../../../common/LifeCycle/LifeCycle";
import { ModalBody } from "..";
import { StyledLabel } from "../../../pages/CaseDetails/styles";
import _ from "lodash";

const getLifecycleStages = (resolutionKind) => {
  const prefix =
    resolutionKind === "mediation"
      ? "Mediation"
      : resolutionKind === "arbitration"
        ? "Arbitration"
        : "Negotiation";
  const lowerPrefix = prefix.toLowerCase();

  return [
    {
      key: "initiated",
      label: `${prefix} Initiated`,
      description: `Notice issued, ${lowerPrefix} invoked, tribunal in process of formation.`,
    },
    {
      key: "in_progress",
      label: `${prefix} in Progress`,
      description:
        "Pleadings, witness statements, hearings — active proceedings.",
      subStages: [
        { label: "Pleadings" },
        { label: "Witness Statements" },
        { label: "Hearings" },
        { label: "Award Reserved" },
      ],
    },
    {
      key: "completed",
      label: `${prefix} Completed`,
      description: "Award rendered. Implementation period.",
    },
    {
      key: "closed",
      label: "Resolution Closed",
      description: "Case closed. All actions concluded.",
    },
  ];
};

const LifecyclePopoverModal = ({
  open,
  onClose,
  items,
  resolutionKind,
  selectedIndex,
  isMediation,
  status,
  currentStateKey,
  setStatusModal,
  caseObj,
  currentState,
}) => {
  const lifecycleStages = getLifecycleStages(resolutionKind);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${resolutionKind === "mediation" ? "Mediation" : resolutionKind === "arbitration" ? "Arbitration" : "Negotiation"} Lifecycle`}
      subtitle={`Current stage: ${currentState?.key === caseObj?.status ? currentState?.text : _.startCase(caseObj?.status)}`}
      width={780}
    >
      <ModalBody>
        <StyledLabel>Case Lifecycle</StyledLabel>
        <CaseLifeCycle
          {...{
            items,
            resolutionKind,
            selectedIndex,
            isMediation,
            status,
            currentStateKey,
            setStatusModal,
            caseObj,
          }}
          onClose={onClose}
        />

        {resolutionKind === "arbitration" && (
          <DetailsContainer>
            {lifecycleStages.map((stage, i) => {
              const isPast = i < selectedIndex - 1;
              const isCurrent = i === selectedIndex - 1;

              return (
                <StageCard
                  key={stage.key}
                  $isCurrent={isCurrent}
                  $isPast={isPast}
                >
                  <StageHeader>
                    <StageTitle $isCurrent={isCurrent} $isPast={isPast}>
                      {stage.label}
                    </StageTitle>
                    {isCurrent && <CurrentBadge>CURRENT STAGE</CurrentBadge>}
                    {isPast && <CompletedBadge>COMPLETED</CompletedBadge>}
                  </StageHeader>

                  <StageDescription>{stage.description}</StageDescription>

                  {stage.subStages && isCurrent && (
                    <SubStagesContainer>
                      <SubTitle>SUB-STAGES</SubTitle>
                      <SubStagesGrid>
                        {stage.subStages.map((sub, j) => {
                          const currentSubStage =
                            selectedIndex > 1 ? selectedIndex - 2 : 0;

                          const subIsPast = j < currentSubStage;
                          const subIsCurrent = j === currentSubStage;
                          return (
                            <SubStageItem
                              key={j}
                              $isCurrent={subIsCurrent}
                              $isPast={subIsPast}
                            >
                              {subIsPast ? (
                                <Check size={14} />
                              ) : subIsCurrent ? (
                                <ActiveDot />
                              ) : (
                                <EmptyDot />
                              )}
                              <span>{sub.label}</span>
                            </SubStageItem>
                          );
                        })}
                      </SubStagesGrid>
                    </SubStagesContainer>
                  )}
                </StageCard>
              );
            })}
          </DetailsContainer>
        )}
      </ModalBody>
    </Modal>
  );
};

export default LifecyclePopoverModal;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StageCard = styled.div`
  border-radius: 10px;
  padding: 20px;
  border: 1px solid
    ${({ $isCurrent }) => ($isCurrent ? "#00818A" : COLORS.BORDER_GREY)};
  background: ${({ $isCurrent, $isPast }) =>
    $isCurrent ? "#E0F2F1" : $isPast ? "#F0FDF4" : "white"};
`;

const StageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const StageTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ $isCurrent, $isPast }) =>
    $isCurrent ? "#006970" : $isPast ? "#15803D" : "#1F2937"};
`;

const CurrentBadge = styled.span`
  background: #00818a;
  color: white;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
`;

const CompletedBadge = styled.span`
  background: #15803d;
  color: white;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
`;

const StageDescription = styled.div`
  font-size: 13.5px;
  color: #6b7280;
  line-height: 1.55;
`;

const SubStagesContainer = styled.div`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #bae6fd;
`;

const SubTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 12px;
`;

const SubStagesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const SubStageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ $isCurrent, $isPast }) =>
    $isCurrent ? "#006970" : $isPast ? "#15803D" : "#6B7280"};
  font-weight: ${({ $isCurrent }) => ($isCurrent ? 600 : 500)};
`;

const ActiveDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #00818a;
`;

const EmptyDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
`;
